import { queryClient } from "@/lib/api/queryClient";
import { getUser } from "@/lib/api/clients/UserClient";
import { DEFAULT_FIELDS } from "@/lib/query/hooks/useUser";
import { qk } from "@/lib/query/queryKeys";
import { getPref, setPref } from "@/lib/storage/userPrefs";
import { tokenStore } from "@/lib/auth/tokenStore";
import {
  ACCOUNT_STAMP_KEY,
  clearAccountScopedState,
} from "@/lib/storage/accountScopedState";
import { setTheme } from "@/lib/theme/themeStore";
import { setLocale } from "@/lib/i18n/localeStore";
import type { User } from "@/types/User";

/**
 * Hard cap on how long a login may wait for the account's presentation. The
 * auth forms already show a spinner while this runs, so the wait is invisible
 * — but a slow or dead `/user` must never hold the user on the login screen.
 */
const HYDRATION_TIMEOUT_MS = 1_500;

/**
 * Sweeps the previous account's browser state when a DIFFERENT account signs
 * in, and remembers who this browser now belongs to.
 *
 * Login is the only honest place for this. The obvious alternative —
 * tearing down on `veltric:auth-lost` — fires for a mistyped password and for
 * a transient refresh failure too (the 401 interceptor has no auth-route
 * exemption), which would destroy the CURRENT user's unsaved ad-hoc views and
 * every wizard draft in the tab. It also misses the most common ending of all:
 * a refresh cookie that simply expired between visits never fires anything.
 * Keyed on identity, both cases land right: the same person keeps their
 * views, a different one starts clean.
 */
function reconcileAccountIdentity(id: string | null | undefined): boolean {
  // Strings only. The id must be the hex `userId` the auth response carries —
  // never `user.userId` from `/user/get-user`: main-backend's `User.userId` is
  // a raw ObjectId with no `@JsonbTypeAdapter`, so it arrives as an object,
  // and comparing/storing that would stamp "[object Object]" and then sweep on
  // every single login.
  if (typeof id !== "string" || id.length === 0) return false;

  // One namespace: always a userId, never a companyId. Two users of the same
  // company would otherwise compare equal and never trigger a sweep.
  const previous =
    getPref(ACCOUNT_STAMP_KEY) ??
    // No stamp yet: a browser whose last session simply expired between
    // visits was never swept and never stamped, so fall back to the id that
    // login wrote last. Without this the first hand-over after this code
    // ships inherits everything and then stamps — a leak that never heals.
    tokenStore.getUserId();

  if (previous === id) return false;
  // Genuinely nothing known about this browser: only record who it belongs to.
  // Sweeping here would punish the current user for a first login.
  const switched = previous !== null;
  if (switched) clearAccountScopedState();
  setPref(ACCOUNT_STAMP_KEY, id);
  return switched;
}

/**
 * Applies the account's theme + language BEFORE the first authenticated route
 * paints, and seeds `qk.user.me` on the way through.
 *
 * MUST be awaited BEFORE `markAuthenticated`. Flipping the auth state first
 * makes `PublicOnlyRoute` render `<Navigate to="/">` immediately (App.tsx),
 * so the dashboard mounts while this is still in flight — which is exactly
 * the flash this function exists to remove.
 *
 * Fail-open by construction: any error or a slow response just falls through
 * to `ThemeSync` / `LocaleSync` reconciling after mount. Never throws, never
 * blocks the login. The identity check still runs on the timeout path, using
 * the ids the auth response already carried — and its verdict
 * (`accountSwitched`) is returned on every path.
 *
 * House pattern (see GeneralCard.tsx): await the client call, then
 * `setQueryData` — this repo has no `prefetchQuery`/`ensureQueryData`.
 */
export type HydrationResult = {
  /**
   * A DIFFERENT account signed in on this browser (the previous one's state was
   * just swept). Billing-comms Teil 2a: the auth forms then ignore `?next=` —
   * a deep link written for the previous account is not the new one's to follow.
   */
  accountSwitched: boolean;
};

export async function hydrateAccountPresentation(identity?: {
  userId?: string;
}): Promise<HydrationResult> {
  let user: User | null = null;
  try {
    user = await Promise.race([
      getUser(DEFAULT_FIELDS),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), HYDRATION_TIMEOUT_MS),
      ),
    ]);
  } catch {
    // Presentation is a nicety; the session is already valid.
    user = null;
  }

  // Identity FIRST: the sweep resets theme and locale, so running it after we
  // applied this account's values would undo them.
  const accountSwitched = reconcileAccountIdentity(identity?.userId);

  if (!user) return { accountSwitched };

  queryClient.setQueryData(qk.user.me, user);

  if (user.theme === "light" || user.theme === "dark") {
    setTheme(user.theme);
  }
  if (user.languageCode) {
    // setLocale ignores anything outside SUPPORTED_LOCALES.
    setLocale(user.languageCode);
  }
  return { accountSwitched };
}
